import { api } from './client';
import type { EventByCode, EventDetail, EventRow, HoleFlag } from './types';

export const eventsApi = {
  create: (input: {
    name: string;
    numHoles: number;
    format: 'best_ball_4some' | 'stroke_play';
    handicapEnabled: boolean;
    pars: number[];
    logoUrl?: string | null;
    allowGuestTeams?: boolean;
    holeFlags?: HoleFlag[];
  }) => api.post<{ event: EventRow }>('/events', input),

  mine: () => api.get<{ events: EventRow[] }>('/events/mine'),
  get: (id: string) => api.get<EventDetail>(`/events/${id}`),
  byCode: (code: string) =>
    api.get<EventByCode>(`/events/by-code/${encodeURIComponent(code)}`),
  patch: (
    id: string,
    fields: Partial<{
      name: string;
      logoUrl: string | null;
      handicapEnabled: boolean;
      pars: number[];
      allowGuestTeams: boolean;
      holeFlags: HoleFlag[];
    }>,
  ) => api.patch<{ event: EventRow }>(`/events/${id}`, fields),

  join: (input: {
    code: string;
    displayName?: string;
    handicap?: number;
    teamId?: string;
    createTeamName?: string;
  }) =>
    api.post<{
      event: { id: string; shortCode: string; name: string; format: string; status: string };
      participant: { id: string; handicap: number | null };
      team: { id: string; name: string } | null;
    }>('/events/join', input),

  start: (id: string) => api.post<{ event: EventRow }>(`/events/${id}/start`),
  finalize: (id: string) => api.post<{ event: EventRow }>(`/events/${id}/finalize`),
  remove: (id: string) => api.del<void>(`/events/${id}`),

  uploadUrl: (filename: string, contentType: string) =>
    api.post<{ uploadUrl: string; token: string; path: string; publicUrl: string }>(
      '/events/upload-url',
      { filename, contentType },
    ),

  createTeam: (id: string, name: string) =>
    api.post<{ team: { id: string; name: string } }>(`/events/${id}/teams`, { name }),
  patchTeam: (id: string, teamId: string, name: string) =>
    api.patch<{ team: { id: string; name: string } }>(`/events/${id}/teams/${teamId}`, { name }),
  deleteTeam: (id: string, teamId: string) =>
    api.del<void>(`/events/${id}/teams/${teamId}`),
  joinTeam: (id: string, teamId: string) =>
    api.post<{ teamId: string }>(`/events/${id}/teams/${teamId}/join`),
  leaveTeam: (id: string, teamId: string) =>
    api.post<void>(`/events/${id}/teams/${teamId}/leave`),

  patchParticipant: (
    id: string,
    pid: string,
    fields: Partial<{ handicap: number | null; displayName: string }>,
  ) => api.patch<{ ok: true }>(`/events/${id}/participants/${pid}`, fields),

  createPlayer: (
    id: string,
    input: { displayName: string; teamId?: string | null; handicap?: number },
  ) => api.post<{ participantId: string }>(`/events/${id}/players`, input),

  deletePlayer: (id: string, pid: string) =>
    api.del<void>(`/events/${id}/participants/${pid}`),

  claimSlot: (id: string, pid: string) =>
    api.post<{ participantId: string; teamId: string | null }>(
      `/events/${id}/participants/${pid}/claim`,
    ),
};
